/**
 * TasksFilterBar — Single source of truth for ALL filters across the
 * Reports > Tasks sub-tabs (Volume, Quality, Velocity, Deadlines, Capacity).
 *
 * Mirrors the visual/UX pattern of AttendanceFilterBar (consistency across
 * the Reports hub) and is fully controlled by the parent.
 */
import React, { useMemo } from 'react';
import {
  Calendar, User, Briefcase, Building2, Search, Filter as FilterIcon,
  X, Zap, Flag, Layers,
} from 'lucide-react';
import { formatDateToLocal } from '../../lib/dateUtils';

export const TASK_FILTER_DEFAULTS = {
  assigneeId: 'all',
  clientId: 'all',
  department: 'all',
  taskType: 'all',
  subtype: 'all',
  status: 'all',
  priority: 'all',
  search: '',
};

const PRESETS = [
  { key: '7d',  label: 'Last 7d',  compute: () => offset(7) },
  { key: '30d', label: 'Last 30d', compute: () => offset(30) },
  { key: '90d', label: 'Last 90d', compute: () => offset(90) },
  { key: 'mtd', label: 'MTD',      compute: () => monthToDate() },
  { key: 'qtd', label: 'QTD',      compute: () => quarterToDate() },
  { key: 'ytd', label: 'YTD',      compute: () => yearToDate() },
];

function offset(days) {
  const to = new Date();
  const from = new Date(); from.setDate(from.getDate() - days);
  return [formatDateToLocal(from), formatDateToLocal(to)];
}
function monthToDate() {
  const t = new Date();
  return [formatDateToLocal(new Date(t.getFullYear(), t.getMonth(), 1)), formatDateToLocal(t)];
}
function quarterToDate() {
  const t = new Date();
  const qStart = new Date(t.getFullYear(), Math.floor(t.getMonth() / 3) * 3, 1);
  return [formatDateToLocal(qStart), formatDateToLocal(t)];
}
function yearToDate() {
  const t = new Date();
  return [formatDateToLocal(new Date(t.getFullYear(), 0, 1)), formatDateToLocal(t)];
}

const SegBtn = ({ active, onClick, children, testid }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className={`px-2.5 py-1 text-[11px] rounded-md border transition-all ${
      active
        ? 'bg-pink-50 text-pink-700 border-pink-300 font-medium shadow-sm'
        : 'bg-white text-neutral-600 border-neutral-200 hover:border-neutral-300 hover:bg-neutral-50'
    }`}
  >
    {children}
  </button>
);

const FieldLabel = ({ icon: Icon, children }) => (
  <div className="flex items-center gap-1 text-[10px] font-medium text-neutral-500 uppercase tracking-wider mb-1">
    {Icon && <Icon className="w-3 h-3" />}
    {children}
  </div>
);

const Field = ({ icon, label, children, className = '' }) => (
  <div className={className}>
    <FieldLabel icon={icon}>{label}</FieldLabel>
    {children}
  </div>
);

const countActive = (f) => {
  let n = 0;
  for (const k of ['assigneeId', 'clientId', 'department', 'taskType', 'subtype', 'status', 'priority']) {
    if (f[k] && f[k] !== 'all') n++;
  }
  if (f.search) n++;
  return n;
};

const TasksFilterBar = ({
  filters, setFilters,
  employees = [], clients = [], departments = [],
  taskTypes = ['Social Media', 'Production'],
  subtypes = [],
  statuses = ['New', 'In Progress', 'Review', 'Revision', 'Completed'],
  priorities = ['Low', 'Medium', 'High', 'Urgent'],
}) => {
  const update = (patch) => setFilters((prev) => ({ ...prev, ...patch }));

  const activePresetKey = useMemo(() => {
    for (const p of PRESETS) {
      const [f, t] = p.compute();
      if (f === filters.fromDate && t === filters.toDate) return p.key;
    }
    return null;
  }, [filters.fromDate, filters.toDate]);

  const activeCount = countActive(filters);

  const clearAll = () => setFilters((prev) => ({
    ...prev,
    ...TASK_FILTER_DEFAULTS,
    fromDate: prev.fromDate,
    toDate: prev.toDate,
  }));

  return (
    <div
      className="bg-white border border-neutral-200 rounded-xl shadow-sm overflow-hidden"
      data-testid="tasks-filter-bar"
    >
      {/* Header strip */}
      <div className="flex items-center justify-between bg-gradient-to-r from-pink-50/60 via-white to-white border-b border-neutral-100 px-4 py-2">
        <div className="flex items-center gap-2 text-[13px] font-semibold text-[#1a1a2e]">
          <FilterIcon className="w-4 h-4 text-pink-600" />
          Filters
          {activeCount > 0 && (
            <span className="ml-1 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-pink-600 text-white text-[10px] font-bold">
              {activeCount}
            </span>
          )}
        </div>
        {activeCount > 0 && (
          <button
            onClick={clearAll}
            data-testid="tasks-filter-clear-all"
            className="inline-flex items-center gap-1 text-[11px] text-neutral-500 hover:text-pink-700 transition-colors"
          >
            <X className="w-3 h-3" /> Clear all
          </button>
        )}
      </div>

      <div className="p-4 space-y-4">
        {/* Row 1 — Dates */}
        <div className="flex flex-wrap items-end gap-3">
          <Field icon={Calendar} label="From">
            <input
              type="date" value={filters.fromDate}
              onChange={(e) => update({ fromDate: e.target.value })}
              data-testid="tasks-filter-from-date"
              className="px-3 py-2 border border-neutral-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-pink-200"
            />
          </Field>
          <Field icon={Calendar} label="To">
            <input
              type="date" value={filters.toDate}
              onChange={(e) => update({ toDate: e.target.value })}
              data-testid="tasks-filter-to-date"
              className="px-3 py-2 border border-neutral-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-pink-200"
            />
          </Field>
          <div className="flex items-center gap-1.5 pb-0.5">
            {PRESETS.map((p) => (
              <SegBtn
                key={p.key}
                active={activePresetKey === p.key}
                onClick={() => {
                  const [f, t] = p.compute();
                  update({ fromDate: f, toDate: t });
                }}
                testid={`tasks-filter-preset-${p.key}`}
              >
                {p.label}
              </SegBtn>
            ))}
          </div>
        </div>

        {/* Row 2 — Who/What */}
        <div className="flex flex-wrap items-end gap-3">
          <Field icon={User} label="Assignee" className="flex-1 min-w-[240px]">
            <select
              value={filters.assigneeId}
              onChange={(e) => update({ assigneeId: e.target.value })}
              data-testid="tasks-filter-assignee"
              className="w-full px-3 py-2 border border-neutral-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-pink-200"
            >
              <option value="all">🌐 All assignees ({employees.length})</option>
              <optgroup label="Individual">
                {employees.map((e) => (
                  <option key={e._id} value={e._id}>
                    {e.personal?.full_name || e.work_email || e._id}
                  </option>
                ))}
              </optgroup>
            </select>
          </Field>
          <Field icon={Briefcase} label="Client" className="min-w-[180px]">
            <select
              value={filters.clientId}
              onChange={(e) => update({ clientId: e.target.value })}
              data-testid="tasks-filter-client"
              className="w-full px-3 py-2 border border-neutral-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-pink-200"
            >
              <option value="all">All clients</option>
              {clients.map((c) => (
                <option key={c._id} value={c._id}>{c.name || c.company_name || c._id}</option>
              ))}
            </select>
          </Field>
          <Field icon={Building2} label="Department" className="min-w-[170px]">
            <select
              value={filters.department}
              onChange={(e) => update({ department: e.target.value })}
              data-testid="tasks-filter-department"
              className="w-full px-3 py-2 border border-neutral-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-pink-200"
            >
              <option value="all">All departments</option>
              {departments.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </Field>
          <Field icon={Search} label="Search" className="flex-1 min-w-[220px]">
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 w-3.5 h-3.5 text-neutral-400 pointer-events-none" />
              <input
                type="text" value={filters.search}
                onChange={(e) => update({ search: e.target.value })}
                placeholder="Task title…"
                data-testid="tasks-filter-search"
                className="w-full pl-8 pr-3 py-2 border border-neutral-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-pink-200"
              />
            </div>
          </Field>
        </div>

        {/* Row 3 — Type/Status */}
        <div className="pt-3 border-t border-dashed border-neutral-200">
          <div className="text-[10px] font-medium text-neutral-400 uppercase tracking-wider mb-2 flex items-center gap-1">
            <Zap className="w-3 h-3" /> Task attributes
          </div>
          <div className="flex flex-wrap items-end gap-3">
            <Field icon={Layers} label="Type">
              <select
                value={filters.taskType}
                onChange={(e) => update({ taskType: e.target.value })}
                data-testid="tasks-filter-type"
                className="px-3 py-2 border border-neutral-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-pink-200"
              >
                <option value="all">Any</option>
                {taskTypes.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </Field>
            <Field label="Subtype">
              <select
                value={filters.subtype}
                onChange={(e) => update({ subtype: e.target.value })}
                data-testid="tasks-filter-subtype"
                className="px-3 py-2 border border-neutral-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-pink-200"
              >
                <option value="all">Any</option>
                {subtypes.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </Field>
            <Field label="Status">
              <select
                value={filters.status}
                onChange={(e) => update({ status: e.target.value })}
                data-testid="tasks-filter-status"
                className="px-3 py-2 border border-neutral-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-pink-200"
              >
                <option value="all">Any</option>
                {statuses.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
            <Field icon={Flag} label="Priority">
              <select
                value={filters.priority}
                onChange={(e) => update({ priority: e.target.value })}
                data-testid="tasks-filter-priority"
                className="px-3 py-2 border border-neutral-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-pink-200"
              >
                <option value="all">Any</option>
                {priorities.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </Field>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TasksFilterBar;
